import type { FC } from "react";
import { useNavigate } from "react-router";

import type { SiteTableEntity } from "src/app/features/Site/types/SiteTableEntity";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { ExportButton } from "../../../FeatureButtons/ExportButton";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import {
    displayDateTimeValue,
    displayOrgUnitUpdateStatusValue,
} from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";

const getColumnsConfig = (t: Function): ExtendedColumnsType<SiteTableEntity>[] => {
    return [
        {
            title: t("oranizationUnitList.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("oranizationUnitList.name"),
            key: "name",
            dataIndex: "name",
        },
        {
            title: t("oranizationUnitList.siteId"),
            key: "siteId",
            dataIndex: "siteId",
        },
        {
            title: t("oranizationUnitList.organizationUnit.name"),
            key: "parentUnitName",
            dataIndex: "parentUnitName",
        },
        {
            title: t("oranizationUnitList.dealerName"),
            key: "dealerName",
            dataIndex: "dealerName",
        },
        {
            title: t("oranizationUnitList.city"),
            key: "city",
            dataIndex: "city",
        },
        {
            title: t("list.form.status"),
            key: "status",
            dataIndex: "status",
            render: (value) => displayOrgUnitUpdateStatusValue(value),
        },
        {
            title: t("list.form.changed"),
            key: "changed",
            dataIndex: "changed",
            render: (value) => displayDateTimeValue(value),
        },
    ];
};

export interface SitesTableViewProps extends AntCustomFormField<SiteTableEntity[]>, CommonFieldAttributes {}

export const SitesTableView: FC<SitesTableViewProps> = ({ value = [], readOnly }) => {
    const navigate = useNavigate();
    const onRowClick = (record: SiteTableEntity) => {
        const resource = record.unitType === "SITE_GROUP" ? ResourceName.SITE_GROUPS : ResourceName.SITES;
        navigate(`${BASE_PATH}/${resource}/${FORM_ACTION.EDIT}/${record.id}?id=${record.id}`);
    };

    const exportTable = () => {
        // TODO: AP3ADMIN-1757 replace this implementation with something more robust, Viz.: https://datatracker.ietf.org/doc/html/rfc4180
        const csvContent = `data:text/csv;charset=utf-8,${value.map((e) => Object.values(e).join(",")).join("\n")}`;
        const a = document.createElement("a");
        const encodedUri = encodeURI(csvContent);
        a.setAttribute("href", encodedUri);
        a.download = "export.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const exportButton = <ExportButton callback={exportTable} />;

    return (
        <PropertyTable<SiteTableEntity>
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="id"
            tableData={value}
            readOnly={readOnly || false}
            customActions={[exportButton]}
            onDataChanged={() => {}}
            onRow={(record: SiteTableEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
        />
    );
};
